import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  CreditCard, Plus, X, Save, Loader2, Trash2, Send, Copy,
  ExternalLink, CheckCircle2, Edit, History as HistoryIcon, RefreshCcw,
  ChevronDown, ChevronUp, User, Mail, Phone, MapPin, Calendar, Receipt,
  Undo2,
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  draft:           { style: 'bg-slate-100 text-slate-700 border-slate-300',  label: 'Brouillon' },
  sent:            { style: 'bg-blue-100 text-blue-800 border-blue-300',     label: 'Envoyée' },
  partially_paid:  { style: 'bg-amber-100 text-amber-900 border-amber-300',  label: 'Partiellement payée' },
  paid:            { style: 'bg-emerald-100 text-emerald-800 border-emerald-300', label: 'Payée intégralement' },
  cancelled:       { style: 'bg-rose-100 text-rose-700 border-rose-300',     label: 'Annulée' },
};

const fmtEUR = (n) => `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const todayISO = () => new Date().toISOString().slice(0, 10);

const buildDefaultSchedule = (total, departureDate) => {
  const a1 = Math.round(total * 0.4 * 100) / 100;
  const a2 = Math.round(total * 0.3 * 100) / 100;
  const a3 = Math.round((total - a1 - a2) * 100) / 100;
  const today = new Date();
  const plus30 = new Date(today.getTime() + 30 * 86400000).toISOString().slice(0, 10);
  let due3 = new Date(today.getTime() + 60 * 86400000).toISOString().slice(0, 10);
  if (departureDate) {
    const dep = new Date(departureDate);
    if (!Number.isNaN(dep.getTime())) {
      due3 = new Date(dep.getTime() - 60 * 86400000).toISOString().slice(0, 10);
    }
  }
  return [
    { type: 'payment', label: 'Acompte (40 %)',     amount: a1, due_date: todayISO(), status: 'pending' },
    { type: 'payment', label: '2e versement (30 %)', amount: a2, due_date: plus30,    status: 'pending' },
    { type: 'payment', label: 'Solde (30 %)',        amount: a3, due_date: due3,      status: 'pending' },
  ];
};

const emptyForm = () => ({
  client_name: '',
  client_email: '',
  client_phone: '',
  client_address: '',
  title: 'Croisière SOGNUDIMARE',
  cruise_name: '',
  cruise_date_start: '',
  cruise_date_end: '',
  total: 0,
  installments: buildDefaultSchedule(0, ''),
  notes: '',
  client_message: '',
});

// ─────────────────────────────────────────────────────────────────
//  Form modal
// ─────────────────────────────────────────────────────────────────
const CommandeForm = ({ open, onClose, onSaved, initial, token }) => {
  const [data, setData] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [customSchedule, setCustomSchedule] = useState(false);

  useEffect(() => {
    if (open) {
      if (initial) {
        setData({
          ...initial,
          installments: initial.installments?.map((i) => ({
            type: i.type || 'payment',
            label: i.label,
            amount: i.amount,
            due_date: i.due_date,
            status: i.status || 'pending',
            square_payment_link: i.square_payment_link,
            paid_at: i.paid_at,
          })) || buildDefaultSchedule(initial.total || 0, initial.cruise_date_start),
        });
        setCustomSchedule(false);
      } else {
        setData(emptyForm());
        setCustomSchedule(false);
      }
    }
  }, [open, initial]);

  const updField = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const updTotal = (v) => {
    const total = Number(v) || 0;
    setData((d) => ({
      ...d,
      total,
      installments: customSchedule ? d.installments : buildDefaultSchedule(total, d.cruise_date_start),
    }));
  };
  const updDeparture = (v) => {
    setData((d) => ({
      ...d,
      cruise_date_start: v,
      installments: customSchedule ? d.installments : buildDefaultSchedule(d.total || 0, v),
    }));
  };

  const updInstallment = (idx, key, value) => {
    setData((d) => {
      const next = [...d.installments];
      next[idx] = { ...next[idx], [key]: key === 'amount' ? Number(value) || 0 : value };
      return { ...d, installments: next };
    });
  };
  const addInstallment = () => setData((d) => ({
    ...d,
    installments: [...d.installments, { type: 'payment', label: `Versement ${d.installments.length + 1}`, amount: 0, due_date: todayISO(), status: 'pending' }],
  }));
  const addRefund = () => setData((d) => ({
    ...d,
    installments: [...d.installments, { type: 'refund', label: 'Remboursement', amount: 0, due_date: todayISO(), status: 'pending' }],
  }));
  const removeInstallment = (idx) => setData((d) => ({ ...d, installments: d.installments.filter((_, i) => i !== idx) }));
  const resetSchedule = () => {
    setCustomSchedule(false);
    setData((d) => ({ ...d, installments: buildDefaultSchedule(d.total || 0, d.cruise_date_start) }));
  };

  // Totals — refunds are deducted from total
  const sumPayments = data.installments
    .filter((i) => (i.type || 'payment') === 'payment')
    .reduce((s, i) => s + Number(i.amount || 0), 0);
  const sumRefunds = data.installments
    .filter((i) => i.type === 'refund')
    .reduce((s, i) => s + Math.abs(Number(i.amount || 0)), 0);
  const netDue = Math.round((Number(data.total || 0) - sumRefunds) * 100) / 100;
  // The sum of all payment installments should match the NET due (not the gross total),
  // because refunds reduce what the client actually has to pay.
  const diff = Math.round((sumPayments - netDue) * 100) / 100;

  const submit = async () => {
    if (!data.client_name.trim()) return toast.error('Nom du client requis');
    if (!data.client_email.trim()) return toast.error('Email du client requis');
    if (!data.total || data.total <= 0) return toast.error('Le total doit être supérieur à 0');
    setSaving(true);
    try {
      const hdr = { Authorization: `Bearer ${token}` };
      if (initial?.id) {
        await axios.put(`${API}/commandes/${initial.id}`, data, { headers: hdr });
        toast.success('Commande mise à jour');
      } else {
        await axios.post(`${API}/commandes`, data, { headers: hdr });
        toast.success('Commande créée');
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur enregistrement');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-6" data-testid="commande-form-modal">
      <div className="bg-white w-full max-w-4xl max-h-[95vh] overflow-y-auto rounded-2xl shadow-2xl">
        <div className="sticky top-0 z-10 bg-white border-b px-5 sm:px-7 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#C4724F]/10 flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-[#C4724F]" />
            </div>
            <div>
              <h2 className="font-display text-lg font-bold text-[#0A2540]">
                {initial ? `Modifier ${initial.commande_number}` : 'Nouvelle commande'}
              </h2>
              <p className="text-xs text-slate-500">Saisie manuelle d'une réservation</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="commande-form-close"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-5 sm:px-7 py-5 space-y-6">
          {/* CLIENT */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 flex items-center gap-2 mb-3">
              <User className="h-3.5 w-3.5" /> Informations client
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Nom complet *</Label><Input value={data.client_name} onChange={(e) => updField('client_name', e.target.value)} data-testid="client-name-input" /></div>
              <div><Label>Email *</Label><Input type="email" value={data.client_email} onChange={(e) => updField('client_email', e.target.value)} data-testid="client-email-input" /></div>
              <div><Label>Téléphone</Label><Input value={data.client_phone} onChange={(e) => updField('client_phone', e.target.value)} /></div>
              <div><Label>Adresse</Label><Input value={data.client_address} onChange={(e) => updField('client_address', e.target.value)} /></div>
            </div>
          </section>

          {/* RESERVATION */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 flex items-center gap-2 mb-3">
              <Receipt className="h-3.5 w-3.5" /> Réservation
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Label>Intitulé *</Label><Input value={data.title} onChange={(e) => updField('title', e.target.value)} /></div>
              <div className="sm:col-span-2"><Label>Croisière (libre)</Label><Input value={data.cruise_name} onChange={(e) => updField('cruise_name', e.target.value)} placeholder="L'Inoubliable Tour de Corse" /></div>
              <div><Label>Date de départ</Label><Input type="date" value={data.cruise_date_start || ''} onChange={(e) => updDeparture(e.target.value)} /></div>
              <div><Label>Date de retour</Label><Input type="date" value={data.cruise_date_end || ''} onChange={(e) => updField('cruise_date_end', e.target.value)} /></div>
              <div><Label>Montant total TTC (€) *</Label><Input type="number" step="0.01" min="0" value={data.total} onChange={(e) => updTotal(e.target.value)} data-testid="total-input" /></div>
            </div>
          </section>

          {/* ECHEANCIER */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5" /> Échéancier de paiement
              </h3>
              <div className="flex items-center gap-2">
                {customSchedule && (
                  <Button type="button" variant="outline" size="sm" onClick={resetSchedule} className="text-xs h-7" data-testid="reset-schedule-btn">
                    <RefreshCcw className="h-3 w-3 mr-1" /> Template 40/30/30
                  </Button>
                )}
                {!customSchedule && (
                  <Button type="button" variant="outline" size="sm" onClick={() => setCustomSchedule(true)} className="text-xs h-7" data-testid="customize-schedule-btn">
                    <Edit className="h-3 w-3 mr-1" /> Personnaliser
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              {data.installments.map((inst, idx) => {
                const isRefund = inst.type === 'refund';
                const togglePaid = () => {
                  const next = [...data.installments];
                  const wasPaid = next[idx].status === 'paid';
                  next[idx] = {
                    ...next[idx],
                    status: wasPaid ? 'pending' : 'paid',
                    paid_at: wasPaid ? null : new Date().toISOString(),
                  };
                  setData({ ...data, installments: next });
                };
                const isPaid = inst.status === 'paid';
                // Refunds always editable (label/amount/date) even if customSchedule is off
                const fieldsDisabled = !customSchedule && !isRefund;
                const canDelete = customSchedule || isRefund;
                const bgClass = isRefund
                  ? (isPaid ? 'bg-rose-50 border border-rose-300' : 'bg-rose-50/60 border border-rose-200')
                  : (isPaid ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50');
                return (
                  <div
                    key={idx}
                    className={`grid grid-cols-12 gap-2 items-center rounded-lg p-2.5 ${bgClass}`}
                    data-testid={`installment-row-${idx}`}
                  >
                    <div className="col-span-12 sm:col-span-4 flex items-center gap-1.5">
                      {isRefund && <Undo2 className="h-3.5 w-3.5 text-rose-600 shrink-0" />}
                      <Input
                        value={inst.label}
                        disabled={fieldsDisabled}
                        onChange={(e) => updInstallment(idx, 'label', e.target.value)}
                        placeholder={isRefund ? 'Motif du remboursement' : 'Intitulé'}
                        className={`h-8 text-sm ${isRefund ? 'border-rose-300 focus-visible:ring-rose-400' : ''}`}
                      />
                    </div>
                    <div className="col-span-6 sm:col-span-3 relative">
                      {isRefund && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-rose-600 font-bold text-sm pointer-events-none">−</span>}
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={inst.amount}
                        disabled={fieldsDisabled}
                        onChange={(e) => updInstallment(idx, 'amount', e.target.value)}
                        className={`h-8 text-sm ${isRefund ? 'pl-6 border-rose-300 focus-visible:ring-rose-400 text-rose-700 font-semibold' : ''}`}
                      />
                    </div>
                    <div className="col-span-6 sm:col-span-3">
                      <Input
                        type="date"
                        value={inst.due_date}
                        disabled={fieldsDisabled}
                        onChange={(e) => updInstallment(idx, 'due_date', e.target.value)}
                        className={`h-8 text-sm ${isRefund ? 'border-rose-300' : ''}`}
                      />
                    </div>
                    <div className="col-span-12 sm:col-span-2 flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={togglePaid}
                        className={`text-[10px] font-semibold px-2 py-1 rounded border transition-colors ${
                          isPaid
                            ? (isRefund ? 'bg-rose-500 border-rose-500 text-white' : 'bg-emerald-500 border-emerald-500 text-white')
                            : 'bg-white border-slate-300 text-slate-500 hover:border-emerald-400 hover:text-emerald-600'
                        }`}
                        title={isRefund
                          ? (isPaid ? 'Remboursement effectué — cliquer pour annuler' : 'Marquer le remboursement comme effectué')
                          : (isPaid ? 'Cliquer pour repasser en attente' : 'Marquer comme déjà payé')}
                        data-testid={`toggle-paid-${idx}`}
                      >
                        {isRefund
                          ? (isPaid ? '✓ Effectué' : 'À effectuer')
                          : (isPaid ? '✓ Payé' : 'À payer')}
                      </button>
                      {canDelete && data.installments.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeInstallment(idx)}
                          className="p-1 text-rose-500 hover:bg-rose-50 rounded"
                          data-testid={`remove-installment-${idx}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {customSchedule && (
                <Button type="button" variant="outline" size="sm" onClick={addInstallment} className="text-xs h-7" data-testid="add-installment-btn">
                  <Plus className="h-3 w-3 mr-1" /> Ajouter un versement
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addRefund}
                className="text-xs h-7 border-rose-300 text-rose-700 hover:bg-rose-50"
                data-testid="add-refund-btn"
                title="Remboursement / remise exceptionnelle déduit du total"
              >
                <Undo2 className="h-3 w-3 mr-1" /> Ajouter un remboursement
              </Button>
            </div>

            <div className="mt-3 rounded-lg bg-white border p-3 text-sm space-y-1">
              <div className="flex items-center justify-between text-slate-600">
                <span>Total brut</span><span className="font-semibold tabular-nums">{fmtEUR(data.total || 0)}</span>
              </div>
              {sumRefunds > 0 && (
                <div className="flex items-center justify-between text-rose-700">
                  <span>Remboursements</span><span className="font-semibold tabular-nums">− {fmtEUR(sumRefunds)}</span>
                </div>
              )}
              {sumRefunds > 0 && (
                <div className="flex items-center justify-between border-t pt-1.5 mt-1.5 text-[#0A2540] font-bold">
                  <span>Net dû</span><span className="tabular-nums">{fmtEUR(netDue)}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-slate-600 pt-1.5 border-t mt-1.5">
                <span>Somme des versements facturés</span>
                <span className="font-semibold tabular-nums">{fmtEUR(sumPayments)}</span>
              </div>
              {diff !== 0 && (
                <div className={`text-xs font-semibold ${diff > 0 ? 'text-amber-600' : 'text-rose-600'}`} data-testid="diff-warning">
                  ⚠ Écart avec le net dû : {diff > 0 ? '+' : ''}{fmtEUR(diff)}
                </div>
              )}
            </div>
          </section>

          {/* MESSAGE AU CLIENT */}
          <section>
            <Label className="flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5 text-[#C4724F]" />
              Message au client <span className="text-[10px] text-slate-400 font-normal">(s'affiche dans l'email)</span>
            </Label>
            <Textarea
              rows={4}
              value={data.client_message}
              onChange={(e) => updField('client_message', e.target.value)}
              placeholder="Ex. Bonjour Jean, nous sommes ravis de vous accueillir à bord cet été. Voici votre échéancier — n'hésitez pas à nous appeler si vous avez la moindre question. — L'équipe SOGNUDIMARE"
              className="mt-1"
              data-testid="client-message-input"
            />
          </section>

          {/* NOTES INTERNES */}
          <section>
            <Label>Notes internes <span className="text-[10px] text-slate-400 font-normal">(privé, n'apparaît pas dans l'email)</span></Label>
            <Textarea rows={2} value={data.notes} onChange={(e) => updField('notes', e.target.value)} placeholder="Remarques privées sur la commande" />
          </section>
        </div>

        <div className="sticky bottom-0 bg-white border-t px-5 sm:px-7 py-3 flex items-center justify-end gap-2 rounded-b-2xl">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#C4724F] hover:bg-[#A0552F] text-white" data-testid="save-commande-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
            {initial ? 'Mettre à jour' : 'Créer la commande'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
//  Row card (one commande)
// ─────────────────────────────────────────────────────────────────
const CommandeCard = ({ c, onEdit, onDelete, onRefresh, token }) => {
  const [open, setOpen] = useState(false);
  const [acting, setActing] = useState(null); // 'email' | `link-${idx}` | `paid-${idx}`
  const status = statusConfig[c.status] || statusConfig.draft;
  const installments = c.installments || [];
  const payments = installments.filter((i) => (i.type || 'payment') === 'payment');
  const refunds = installments.filter((i) => i.type === 'refund');
  const totalPaid = payments.filter((i) => i.status === 'paid').reduce((s, i) => s + Number(i.amount || 0), 0);
  const totalRefunds = refunds.reduce((s, i) => s + Math.abs(Number(i.amount || 0)), 0);
  const refundsCompleted = refunds.filter((i) => i.status === 'paid').reduce((s, i) => s + Math.abs(Number(i.amount || 0)), 0);
  const netDue = Math.round((Number(c.total || 0) - totalRefunds) * 100) / 100;
  const remaining = Math.round((netDue - totalPaid) * 100) / 100;
  const hdr = { Authorization: `Bearer ${token}` };

  const generateLink = async (idx) => {
    setActing(`link-${idx}`);
    try {
      const r = await axios.post(`${API}/commandes/${c.id}/payment-link/${idx}`, {}, { headers: hdr });
      await navigator.clipboard.writeText(r.data.payment_link);
      toast.success('Lien créé et copié dans le presse-papier');
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur Square');
    } finally { setActing(null); }
  };
  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Lien copié');
  };
  const markPaid = async (idx) => {
    const inst = installments[idx] || {};
    const isRefund = inst.type === 'refund';
    const confirmMsg = isRefund
      ? `Marquer ce remboursement (${fmtEUR(inst.amount)}) comme effectué ?`
      : 'Marquer ce versement comme payé ?';
    if (!window.confirm(confirmMsg)) return;
    setActing(`paid-${idx}`);
    try {
      await axios.post(`${API}/commandes/${c.id}/installments/${idx}/mark-paid`, {}, { headers: hdr });
      toast.success(isRefund ? 'Remboursement marqué comme effectué' : 'Versement marqué comme payé');
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setActing(null); }
  };
  const sendEmail = async () => {
    if (!c.client_email) return toast.error('Pas d\'email client');
    if (!window.confirm(`Envoyer l'email de commande à ${c.client_email} ?`)) return;
    setActing('email');
    try {
      await axios.post(`${API}/commandes/${c.id}/send-email`, {}, { headers: hdr });
      toast.success(`Email envoyé à ${c.client_email}`);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi email');
    } finally { setActing(null); }
  };

  return (
    <Card className="mb-3 overflow-hidden" data-testid={`commande-card-${c.commande_number}`}>
      <CardContent className="p-0">
        <button onClick={() => setOpen((v) => !v)} className="w-full p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors text-left">
          <div className="w-10 h-10 rounded-xl bg-[#C4724F]/10 flex items-center justify-center shrink-0">
            <CreditCard className="h-5 w-5 text-[#C4724F]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono font-semibold text-[#0A2540] text-sm">{c.commande_number}</span>
              <Badge className={`text-[10px] border ${status.style}`}>{status.label}</Badge>
              {c.email_sent_at && <span className="text-[10px] text-slate-400">• email envoyé</span>}
            </div>
            <div className="text-sm text-slate-700 truncate">
              <strong>{c.client_name}</strong> · {c.title}
              {c.cruise_date_start && <span className="text-slate-500"> · {c.cruise_date_start}</span>}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-base font-display font-bold text-[#0A2540]">{fmtEUR(c.total)}</div>
            {totalRefunds > 0 && (
              <div className="text-[10px] text-rose-600 font-semibold">− {fmtEUR(totalRefunds)} remb.</div>
            )}
            <div className="text-[11px] text-slate-500">
              Reste : <span className={remaining > 0 ? 'text-amber-600 font-semibold' : 'text-emerald-600 font-semibold'}>{fmtEUR(Math.max(remaining, 0))}</span>
            </div>
          </div>
          {open ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
        </button>

        {open && (
          <div className="border-t bg-slate-50/60 p-4 space-y-4">
            {/* Client + actions row */}
            <div className="flex flex-wrap items-start gap-4 justify-between">
              <div className="text-xs space-y-0.5 text-slate-600">
                <div className="flex items-center gap-1.5"><Mail className="h-3 w-3" /> {c.client_email || '—'}</div>
                {c.client_phone && <div className="flex items-center gap-1.5"><Phone className="h-3 w-3" /> {c.client_phone}</div>}
                {c.client_address && <div className="flex items-center gap-1.5"><MapPin className="h-3 w-3" /> {c.client_address}</div>}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => onEdit(c)} className="h-8 text-xs" data-testid={`edit-${c.commande_number}`}>
                  <Edit className="h-3.5 w-3.5 mr-1" /> Modifier
                </Button>
                <Button size="sm" onClick={sendEmail} disabled={acting === 'email'} className="h-8 text-xs bg-[#2A9D8F] hover:bg-[#1F7268] text-white" data-testid={`send-email-${c.commande_number}`}>
                  {acting === 'email' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                  Envoyer email
                </Button>
                <Button size="sm" variant="outline" onClick={() => onDelete(c)} className="h-8 text-xs border-rose-300 text-rose-600 hover:bg-rose-50">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Supprimer
                </Button>
              </div>
            </div>

            {/* Installments */}
            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="px-3 py-2 bg-slate-50 border-b flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Échéancier</span>
                <span className="text-xs text-slate-500">
                  {payments.length} versement(s){refunds.length > 0 && ` · ${refunds.length} remb.`}
                </span>
              </div>
              {installments.map((inst, idx) => {
                const isRefund = inst.type === 'refund';
                const isPaid = inst.status === 'paid';
                return (
                  <div
                    key={idx}
                    className={`px-3 py-2.5 border-b last:border-b-0 flex flex-wrap items-center gap-3 ${isRefund ? 'bg-rose-50/40' : ''}`}
                    data-testid={`commande-installment-${c.commande_number}-${idx}`}
                  >
                    <div className="flex-1 min-w-[180px]">
                      <div className={`text-sm font-medium flex items-center gap-1.5 ${isRefund ? 'text-rose-700' : 'text-[#0A2540]'}`}>
                        {isRefund && <Undo2 className="h-3.5 w-3.5" />}
                        {inst.label}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {isRefund ? 'Remboursement' : 'Échéance'} : {inst.due_date}
                      </div>
                    </div>
                    <div className={`text-base font-bold tabular-nums ${isRefund ? 'text-rose-700' : 'text-[#0A2540]'}`}>
                      {isRefund ? '− ' : ''}{fmtEUR(inst.amount)}
                    </div>
                    {isPaid ? (
                      <Badge className={`text-[10px] ${isRefund ? 'bg-rose-100 text-rose-700 border-rose-300' : 'bg-emerald-100 text-emerald-700 border-emerald-300'}`}>
                        <CheckCircle2 className="h-3 w-3 mr-1 inline" /> {isRefund ? 'Effectué' : 'Payé'}
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]">
                        {isRefund ? 'À effectuer' : 'En attente'}
                      </Badge>
                    )}
                    <div className="flex items-center gap-1.5 ml-auto">
                      {!isRefund && (inst.square_payment_link ? (
                        <>
                          <Button size="sm" variant="outline" onClick={() => copyLink(inst.square_payment_link)} className="h-7 text-[11px]" data-testid={`copy-link-${c.commande_number}-${idx}`}>
                            <Copy className="h-3 w-3 mr-1" /> Copier
                          </Button>
                          <a href={inst.square_payment_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-600">
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        </>
                      ) : (
                        <Button size="sm" onClick={() => generateLink(idx)} disabled={acting === `link-${idx}` || inst.status === 'paid'} className="h-7 text-[11px] bg-[#C4724F] hover:bg-[#A0552F] text-white" data-testid={`generate-link-${c.commande_number}-${idx}`}>
                          {acting === `link-${idx}` ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <CreditCard className="h-3 w-3 mr-1" />}
                          Lien Square
                        </Button>
                      ))}
                      {!isPaid && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => markPaid(idx)}
                          disabled={acting === `paid-${idx}`}
                          className={`h-7 text-[11px] ${isRefund ? 'border-rose-300 text-rose-700 hover:bg-rose-50' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}
                          data-testid={`mark-paid-${c.commande_number}-${idx}`}
                          title={isRefund ? 'Marquer le remboursement comme effectué' : 'Marquer comme payé'}
                        >
                          {acting === `paid-${idx}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
              {/* Recap inside the card */}
              <div className="px-3 py-3 bg-slate-50/80 border-t space-y-1 text-sm">
                <div className="flex items-center justify-between text-slate-600">
                  <span>Total brut</span><span className="font-semibold tabular-nums">{fmtEUR(c.total)}</span>
                </div>
                {totalRefunds > 0 && (
                  <>
                    <div className="flex items-center justify-between text-rose-700">
                      <span>Remboursements{refundsCompleted > 0 && totalRefunds !== refundsCompleted ? ` (dont ${fmtEUR(refundsCompleted)} effectués)` : ''}</span>
                      <span className="font-semibold tabular-nums">− {fmtEUR(totalRefunds)}</span>
                    </div>
                    <div className="flex items-center justify-between font-bold text-[#0A2540] border-t pt-1.5 mt-1.5">
                      <span>Net dû</span><span className="tabular-nums">{fmtEUR(netDue)}</span>
                    </div>
                  </>
                )}
                <div className="flex items-center justify-between text-emerald-700 pt-1.5 border-t mt-1.5">
                  <span>Encaissé</span><span className="font-semibold tabular-nums">{fmtEUR(totalPaid)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Reste à percevoir</span>
                  <span className={`font-bold tabular-nums ${remaining > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                    {fmtEUR(Math.max(remaining, 0))}
                  </span>
                </div>
              </div>
            </div>

            {/* Notes & history */}
            {c.notes && (
              <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-900">
                <strong>Notes :</strong> {c.notes}
              </div>
            )}
            {Array.isArray(c.history) && c.history.length > 0 && (
              <details className="text-[11px] text-slate-500">
                <summary className="cursor-pointer flex items-center gap-1 font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-600">
                  <HistoryIcon className="h-3 w-3" /> Historique ({c.history.length})
                </summary>
                <ul className="mt-2 space-y-1 pl-4 list-disc">
                  {c.history.slice().reverse().map((h, i) => (
                    <li key={i}>
                      <span className="text-slate-400">{(h.ts || '').slice(0, 19).replace('T', ' ')}</span> — {h.action}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────
//  Page
// ─────────────────────────────────────────────────────────────────
const AdminCommandes = () => {
  const { token } = useAuth();
  const [commandes, setCommandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, initial: null });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/commandes`, { headers: { Authorization: `Bearer ${token}` } });
      setCommandes(r.data || []);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDelete = async (c) => {
    if (!window.confirm(`Supprimer définitivement la commande ${c.commande_number} ?`)) return;
    try {
      await axios.delete(`${API}/commandes/${c.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Commande supprimée');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur suppression');
    }
  };

  const filtered = commandes.filter((c) => {
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (c.commande_number || '').toLowerCase().includes(s) ||
      (c.client_name || '').toLowerCase().includes(s) ||
      (c.client_email || '').toLowerCase().includes(s) ||
      (c.title || '').toLowerCase().includes(s) ||
      (c.cruise_name || '').toLowerCase().includes(s)
    );
  });

  const totalCA = commandes.reduce((s, c) => s + Number(c.total || 0), 0);
  const totalRefundsAll = commandes.reduce((s, c) => s + (c.installments || [])
    .filter((i) => i.type === 'refund')
    .reduce((s2, i) => s2 + Math.abs(Number(i.amount || 0)), 0), 0);
  const totalPaid = commandes.reduce((s, c) => s + (c.installments || [])
    .filter((i) => (i.type || 'payment') === 'payment' && i.status === 'paid')
    .reduce((s2, i) => s2 + Number(i.amount || 0), 0), 0);
  const netCA = totalCA - totalRefundsAll;

  return (
    <AdminLayout title="Commandes">
      <div className="space-y-5" data-testid="admin-commandes">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0A2540] flex items-center gap-2">
              <CreditCard className="h-6 w-6 text-[#C4724F]" />
              Commandes
            </h1>
            <p className="text-sm text-slate-500 mt-1">Réservations manuelles avec liens de paiement Square (paiement en plusieurs fois).</p>
          </div>
          <Button onClick={() => setModal({ open: true, initial: null })} className="bg-[#C4724F] hover:bg-[#A0552F] text-white" data-testid="new-commande-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Nouvelle commande
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Card><CardContent className="p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500">Total commandes</div><div className="text-xl font-bold text-[#0A2540]">{commandes.length}</div></CardContent></Card>
          <Card><CardContent className="p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500">CA brut</div><div className="text-xl font-bold text-[#0A2540]">{fmtEUR(totalCA)}</div></CardContent></Card>
          <Card><CardContent className="p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500">Remboursements</div><div className="text-xl font-bold text-rose-600">− {fmtEUR(totalRefundsAll)}</div></CardContent></Card>
          <Card><CardContent className="p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500">CA net</div><div className="text-xl font-bold text-[#0A2540]">{fmtEUR(netCA)}</div></CardContent></Card>
          <Card><CardContent className="p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500">Encaissé</div><div className="text-xl font-bold text-emerald-600">{fmtEUR(totalPaid)}</div></CardContent></Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 bg-white border rounded-xl p-2">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher par n°, client, email, croisière..." className="flex-1 min-w-[200px] h-9" data-testid="search-input" />
          <div className="flex items-center gap-1 flex-wrap">
            {['all', 'draft', 'sent', 'partially_paid', 'paid'].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`text-xs px-2.5 py-1.5 rounded-md border transition-colors ${
                  statusFilter === s ? 'bg-[#0A2540] text-white border-[#0A2540]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
                data-testid={`filter-${s}`}
              >
                {s === 'all' ? `Tous (${commandes.length})` : statusConfig[s]?.label || s}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="p-10 text-center text-slate-500">
            <CreditCard className="h-10 w-10 mx-auto mb-3 text-slate-300" />
            <p className="text-sm">Aucune commande pour le moment.</p>
            <Button onClick={() => setModal({ open: true, initial: null })} className="mt-4 bg-[#C4724F] hover:bg-[#A0552F] text-white">
              <Plus className="h-4 w-4 mr-1.5" /> Créer la première
            </Button>
          </CardContent></Card>
        ) : (
          filtered.map((c) => (
            <CommandeCard key={c.id} c={c} onEdit={(cc) => setModal({ open: true, initial: cc })} onDelete={handleDelete} onRefresh={fetchAll} token={token} />
          ))
        )}
      </div>

      <CommandeForm
        open={modal.open}
        onClose={() => setModal({ open: false, initial: null })}
        onSaved={fetchAll}
        initial={modal.initial}
        token={token}
      />
    </AdminLayout>
  );
};

export default AdminCommandes;
